import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { from, throwError } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

const INVALID_VENDOR_SESSION_MESSAGES = [
  'vendor not found',
  'user not found',
  'vendor does not exist',
  'user does not exist'
];

const INVALID_SESSION_MESSAGES = [
  'invalid jwt token',
  'jwt token is malformed',
  'jwt token signature does not match',
  'authorization header missing or invalid for auth request',
  'missing or invalid x-auth-type header',
  'token is missing required role',
  'token deviceid does not match',
  'token devicetype does not match',
  'token apptype does not match',
  'user is not authenticated or session has expired',
  'invalid vendor session',
  'vendor found but inactive',
  'vendor found but inActive'
];

type SessionInvalidationReason = 'expired' | 'vendor-not-found' | 'invalid-session';

let isSessionInvalidationInProgress = false;

function extractMessage(payload: any): string {
  if (!payload) {
    return '';
  }

  if (typeof payload === 'string') {
    return payload;
  }

  if (typeof payload?.message === 'string') {
    return payload.message;
  }

  if (typeof payload?.error === 'string') {
    return payload.error;
  }

  if (typeof payload?.error?.message === 'string') {
    return payload.error.message;
  }

  if (typeof payload?.exception === 'string') {
    return payload.exception;
  }

  return '';
}

function isInvalidVendorSessionMessage(message: string): boolean {
  const normalized = (message || '').toLowerCase();
  return INVALID_VENDOR_SESSION_MESSAGES.some((pattern) => normalized.includes(pattern));
}

function isInvalidSessionMessage(message: string): boolean {
  const normalized = (message || '').toLowerCase();
  return INVALID_SESSION_MESSAGES.some((pattern) => normalized.includes(pattern));
}

/** Extracts the stable machine-readable errorCode from any response shape. */
function extractErrorCode(payload: any): string | null {
  if (!payload) {
    return null;
  }
  if (typeof payload?.errorCode === 'string' && payload.errorCode.trim()) {
    return payload.errorCode.trim().toUpperCase();
  }
  // Also check nested error object (e.g. error.error.errorCode)
  if (typeof payload?.error?.errorCode === 'string' && payload.error.errorCode.trim()) {
    return payload.error.errorCode.trim().toUpperCase();
  }
  return null;
}

/**
 * Resolves a session-invalidation reason.
 * errorCode (from backend structured response) takes priority over fragile message matching.
 */
function resolveSessionInvalidationReason(
  status: number | undefined,
  message: string,
  errorCode?: string | null
): SessionInvalidationReason | null {
  // --- Primary: code-based matching (reliable) ---
  if (errorCode) {
    if (errorCode === 'SESSION_EXPIRED')    return 'expired';
    if (errorCode === 'VENDOR_NOT_FOUND')   return 'vendor-not-found';
    if (errorCode === 'SESSION_INVALID')    return 'invalid-session';
  }

  // --- Fallback: message-string matching (legacy / business-layer 200 responses) ---
  const normalized = (message || '').toLowerCase();

  if (normalized.includes('jwt token is expired')) {
    return 'expired';
  }

  if (isInvalidVendorSessionMessage(normalized)) {
    return 'vendor-not-found';
  }

  if (isInvalidSessionMessage(normalized)) {
    return 'invalid-session';
  }

  if (status === 401 || status === 403) {
    return 'invalid-session';
  }

  return null;
}

function forceLogoutAndRedirect(
  authService: AuthService,
  router: Router,
  reason: SessionInvalidationReason
): void {
  if (isSessionInvalidationInProgress) {
    return;
  }

  isSessionInvalidationInProgress = true;
  authService.signOut(true);
  void router.navigate(['/sign-in'], { queryParams: { reason } }).finally(() => {
    isSessionInvalidationInProgress = false;
  });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  
  // Skip interceptor for authentication endpoints (but not workforce/register)
  const authEndpoints = ['/login', '/sign-in', '/auth/', '/forgot-password', '/reset-password', '/countries', '/states', 'cities'];
  const isAuthEndpoint = authEndpoints.some(endpoint => req.url.includes(endpoint)) || 
                        (req.url.includes('/register') && !req.url.includes('/workforce/register'));
  const shouldEnforceSessionValidity = !isAuthEndpoint;
  
  const request$ = (req.headers.has('Authorization') || isAuthEndpoint)
    ? next(req)
    : from(Promise.all([authService.getToken(), authService.getDeviceId(), authService.getDevicetype()])).pipe(
        switchMap(([token, deviceId, deviceType]) => {
          console.log('Auth interceptor - User token:', token ? 'exists' : 'null');
          if (token && token !== 'null' && token !== 'undefined') {
            const cloned = req.clone({
              setHeaders: {
                'Authorization': `Bearer ${token}`,
                'X-Auth-Type': 'AUTH',
                'X-App-Type': 'LUNA_VENDOR',
                'X-Device-Id': deviceId ?? '',
                'X-Device-Type': deviceType ?? ''
              },
            });
            return next(cloned);
          }

          // No user token, pass through without modification
          console.log('No user token, request passed through');
          return next(req);
        })
      );

  return request$.pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse) || !shouldEnforceSessionValidity) {
        return;
      }

      const responseMessage = extractMessage(event.body);
      const responseErrorCode = extractErrorCode(event.body);
      const reason = resolveSessionInvalidationReason(event.status, responseMessage, responseErrorCode);
      if (reason) {
        console.error('Invalid session detected from API response, forcing logout');
        forceLogoutAndRedirect(authService, router, reason);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      const errorMessage = extractMessage(error?.error) || extractMessage(error);
      const errorCode = extractErrorCode(error?.error);

      if (shouldEnforceSessionValidity) {
        const reason = resolveSessionInvalidationReason(error.status, errorMessage, errorCode);
        if (reason) {
          console.error('Invalid session detected from API error, forcing logout');
          forceLogoutAndRedirect(authService, router, reason);
        }
      }

      return throwError(() => error);
    })
  );
};


